(() => {
  window.corzoCatalogCardsController?.abort();
  const controller = new AbortController();
  window.corzoCatalogCardsController = controller;

  let generatedPanelId = 0;

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const formatMoney = (cents) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(cents || 0) / 100);

  const getCard = (element) =>
    element.closest(
      '[data-corzo-catalog-card], [data-product-card][data-product-url], [class*="ai-featured-products__card-"][data-product-url]'
    );

  const getHost = (card) =>
    card?.querySelector(
      '[data-corzo-inline-host], .corzo-catalog-card__info, [class*="ai-featured-products-card-content-"], [class*="ai-featured-products__info-"]'
    ) || card;

  const getPanel = (host) =>
    Array.from(host?.children || []).find((child) => child.hasAttribute('data-corzo-options-panel'));

  const getTrigger = (card) => card?.querySelector('[data-corzo-options-button]');

  const closePanel = (panel) => {
    if (!panel) return;
    const host = panel.parentElement;
    const card = getCard(panel);
    panel.hidden = true;
    host?.classList.remove('corzo-catalog-card__inline-host--open');
    card?.classList.remove('corzo-catalog-card--options-open');
    const trigger = getTrigger(card);
    trigger?.setAttribute('aria-expanded', 'false');
  };

  const openPanel = (panel) => {
    if (!panel) return;
    const host = panel.parentElement;
    const card = getCard(panel);
    panel.hidden = false;
    panel.dataset.corzoUserSelected = 'false';
    host?.classList.add('corzo-catalog-card__inline-host--open');
    card?.classList.add('corzo-catalog-card--options-open');
    const trigger = getTrigger(card);
    trigger?.setAttribute('aria-expanded', 'true');
  };

  const selectVariant = (button) => {
    const panel = button.closest('[data-corzo-options-panel]');
    if (!panel || button.disabled) return;

    panel.querySelectorAll('[data-corzo-variant-choice]').forEach((choice) => {
      const selected = choice === button;
      choice.classList.toggle('is-selected', selected);
      choice.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    panel.dataset.corzoSelectedVariantId = button.dataset.corzoVariantId;
    panel.dataset.corzoUserSelected = 'true';
    const title = panel.querySelector('[data-corzo-selected-title]');
    const price = panel.querySelector('[data-corzo-selected-price]');
    const addButton = panel.querySelector('[data-corzo-options-add]');

    if (title) title.textContent = button.dataset.corzoVariantTitle || '';
    if (price) price.textContent = button.dataset.corzoVariantPrice || '';
    if (addButton) addButton.disabled = false;
  };

  const createPanel = (product) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const selectedVariant = variants.find((variant) => variant.available) || variants[0];
    const panelId = `CorzoGeneratedInlineOptions-${product.id}-${generatedPanelId++}`;

    const pills = variants.map((variant) => {
      const selected = selectedVariant && variant.id === selectedVariant.id;
      return `
        <button
          type="button"
          class="corzo-catalog-card__variant-pill${selected ? ' is-selected' : ''}"
          data-corzo-variant-choice
          data-corzo-variant-id="${escapeHtml(variant.id)}"
          data-corzo-variant-title="${escapeHtml(variant.title)}"
          data-corzo-variant-price="${escapeHtml(formatMoney(variant.price))}"
          aria-pressed="${selected ? 'true' : 'false'}"
          ${variant.available ? '' : 'disabled'}
        >
          ${escapeHtml(variant.title)} - ${escapeHtml(formatMoney(variant.price))}
        </button>
      `;
    }).join('');

    return `
      <div
        id="${panelId}"
        class="corzo-catalog-card__options-panel"
        data-corzo-options-panel
        data-corzo-selected-variant-id="${selectedVariant ? escapeHtml(selectedVariant.id) : ''}"
        hidden
      >
        <div class="corzo-catalog-card__selected-option">
          <strong>Tamaño:</strong>
          <span data-corzo-selected-title>${selectedVariant ? escapeHtml(selectedVariant.title) : 'No disponible'}</span>
        </div>
        <div class="corzo-catalog-card__variant-pills" aria-label="Presentaciones disponibles">
          ${pills}
        </div>
        <div class="corzo-catalog-card__selected-price" data-corzo-selected-price>
          ${selectedVariant ? escapeHtml(formatMoney(selectedVariant.price)) : ''}
        </div>
        <label class="corzo-catalog-card__quantity-label" for="${panelId}-Quantity">Cantidad:</label>
        <div class="corzo-catalog-card__quantity">
          <button type="button" data-corzo-quantity-change="-1" aria-label="Disminuir cantidad">−</button>
          <input id="${panelId}-Quantity" type="number" value="1" min="1" max="99" inputmode="numeric" readonly data-corzo-quantity aria-label="Cantidad">
          <button type="button" data-corzo-quantity-change="1" aria-label="Aumentar cantidad">+</button>
        </div>
        <div class="corzo-catalog-card__inline-actions">
          <button type="button" class="corzo-catalog-card__cancel" data-corzo-options-cancel>Cancelar</button>
          <button type="button" class="corzo-catalog-card__add" data-corzo-options-add ${selectedVariant?.available ? '' : 'disabled'}>Agregar</button>
        </div>
      </div>
    `;
  };

  const ensurePanel = async (trigger, card) => {
    const host = getHost(card);
    let panel = getPanel(host);
    if (panel) return panel;

    const handle = trigger.dataset.corzoProductHandle;
    if (!handle) return null;

    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      const product = await response.json();
      host.insertAdjacentHTML('beforeend', createPanel(product));
      panel = getPanel(host);
      if (panel?.id) trigger.setAttribute('aria-controls', panel.id);
      return panel;
    } catch (error) {
      console.error('DYNASTY inline options:', error);
      return null;
    } finally {
      trigger.disabled = false;
      trigger.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-corzo-options-button]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      const card = getCard(trigger);
      const panel = await ensurePanel(trigger, card);
      if (!panel) return;
      panel.hidden ? openPanel(panel) : closePanel(panel);
      return;
    }

    const variantChoice = event.target.closest('[data-corzo-variant-choice]');
    if (variantChoice) {
      event.preventDefault();
      event.stopPropagation();
      selectVariant(variantChoice);
      return;
    }

    const quantityButton = event.target.closest('[data-corzo-quantity-change]');
    if (quantityButton) {
      event.preventDefault();
      event.stopPropagation();
      const panel = quantityButton.closest('[data-corzo-options-panel]');
      const input = panel?.querySelector('[data-corzo-quantity]');
      if (!input) return;
      const change = Number(quantityButton.dataset.corzoQuantityChange || 0);
      input.value = String(Math.max(1, Math.min(99, Number(input.value || 1) + change)));
      return;
    }

    const cancelButton = event.target.closest('[data-corzo-options-cancel]');
    if (cancelButton) {
      event.preventDefault();
      event.stopPropagation();
      closePanel(cancelButton.closest('[data-corzo-options-panel]'));
      return;
    }

    const addButton = event.target.closest('[data-corzo-options-add]');
    if (addButton) {
      event.preventDefault();
      event.stopPropagation();
      const panel = addButton.closest('[data-corzo-options-panel]');
      const input = panel?.querySelector('[data-corzo-quantity]');
      const variantId = panel?.dataset.corzoSelectedVariantId;
      if (!variantId || addButton.disabled) return;

      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: Number(input?.value || 1)
          })
        });
        if (!response.ok) throw new Error('No fue posible agregar la variante');
        window.location.href = '/cart';
      } catch (error) {
        console.error('DYNASTY quick add:', error);
        addButton.disabled = false;
        addButton.removeAttribute('aria-busy');
      }
      return;
    }

    if (event.target.closest('[data-corzo-options-panel]')) return;

    const card = getCard(event.target);
    if (card && !event.target.closest('a, button, input, select, textarea')) {
      window.location.href = card.dataset.productUrl;
    }
  }, { signal: controller.signal, capture: true });

  /*
   * DYNASTY GLOBAL PRODUCT-CARD RULE v2026-08-18
   * Default: every product card uses gallery image 1 as its primary image and
   * gallery image 2 on desktop hover/focus. This applies to existing cards and
   * cards injected later by sections, tabs, search or recommendations.
   * Explicit opt-out only: data-dynasty-secondary-hover="off".
   */
  const hoverCardSelector = [
    '[data-corzo-catalog-card]',
    '[data-product-card]',
    '[data-product-url]',
    '[data-secondary-hover-rule="always"]',
    '[class*="ai-featured-products__card-"]',
    '[class*="ai-featured-products__product-"]',
    '[class*="ai-featured-products-card-"]',
    '[class*="ai-featured-product-card-"]',
    '.resource-card[data-resource-type="product"]',
    '.product-card',
    'product-card'
  ].join(',');

  const hoverWrapperSelector = [
    '.corzo-catalog-card__image-wrap',
    '.resource-card__media',
    '.card-gallery',
    '[class*="product-card-gallery"]',
    '[class*="image-wrapper-"]'
  ].join(',');

  const hoverImageCache = new Map();

  const hoverIsDisabled = (card) =>
    !card ||
    card.matches('[data-dynasty-secondary-hover="off"]') ||
    Boolean(card.closest('[data-dynasty-secondary-hover="off"]'));

  const normalizeHoverImageUrl = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || image.preview_image?.src || image.previewImage?.url || '';
  };

  const getHoverProductHandle = (card) => {
    const rawUrl =
      card?.dataset.productUrl ||
      card?.querySelector('a[href*="/products/"]')?.getAttribute('href') ||
      '';
    const match = String(rawUrl).match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const fetchHoverImage = (handle) => {
    if (!handle) return Promise.resolve('');

    if (!hoverImageCache.has(handle)) {
      hoverImageCache.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' }
        })
          .then((response) => response.ok ? response.json() : null)
          .then((product) => {
            const galleryImage =
              (Array.isArray(product?.images) ? product.images[1] : null) ||
              (Array.isArray(product?.media) ? product.media[1] : null);
            return normalizeHoverImageUrl(galleryImage);
          })
          .catch(() => '')
      );
    }

    return hoverImageCache.get(handle);
  };

  const getHoverPair = (card) => {
    const wrapper = card.matches?.(hoverWrapperSelector)
      ? card
      : card.querySelector(hoverWrapperSelector);
    const primary = wrapper?.querySelector(
      'img:not(.secondary):not(.resource-card__image--secondary):not([data-corzo-hover-secondary])'
    );
    const secondary = wrapper?.querySelector(
      'img.secondary, img.resource-card__image--secondary, img[class*="secondary"], img[data-corzo-hover-secondary]'
    );
    return { wrapper, primary, secondary };
  };

  const activateHoverPair = (card, wrapper, primary, secondary) => {
    wrapper.dataset.corzoHoverWrapper = '';
    primary.dataset.corzoHoverPrimary = '';
    secondary.dataset.corzoHoverSecondary = '';
    secondary.removeAttribute('data-corzo-hover-primary');
    card.dataset.secondaryHoverRule = 'always';
    card.classList.add('corzo-hover-has-secondary');
    card.dataset.corzoHoverReady = 'true';
  };

  const prepareHoverCard = async (card) => {
    if (hoverIsDisabled(card) || card.dataset.corzoHoverLoading === 'true' || card.dataset.corzoHoverReady === 'true') return;

    const { wrapper, primary, secondary: existingSecondary } = getHoverPair(card);
    if (!wrapper || !primary) return;

    if (existingSecondary) {
      activateHoverPair(card, wrapper, primary, existingSecondary);
      return;
    }

    const handle = getHoverProductHandle(card);
    if (!handle) return;

    card.dataset.corzoHoverLoading = 'true';
    const secondImageUrl = await fetchHoverImage(handle);
    delete card.dataset.corzoHoverLoading;

    if (!secondImageUrl || !card.isConnected || !wrapper.isConnected || hoverIsDisabled(card)) return;

    const secondary = primary.cloneNode(false);
    secondary.removeAttribute('srcset');
    secondary.removeAttribute('sizes');
    secondary.removeAttribute('width');
    secondary.removeAttribute('height');
    secondary.src = secondImageUrl;
    secondary.loading = 'eager';
    secondary.decoding = 'async';
    secondary.alt = primary.alt || '';
    secondary.dataset.corzoHoverSecondary = '';
    wrapper.appendChild(secondary);

    activateHoverPair(card, wrapper, primary, secondary);
  };

  const hoverIntersectionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hoverIntersectionObserver.unobserve(entry.target);
          prepareHoverCard(entry.target);
        });
      }, { rootMargin: '320px 0px' })
    : null;

  const registerHoverCard = (card) => {
    if (!(card instanceof Element) || hoverIsDisabled(card) || card.dataset.corzoHoverRegistered === 'true') return;
    card.dataset.corzoHoverRegistered = 'true';

    const { wrapper, primary, secondary } = getHoverPair(card);
    if (wrapper && primary && secondary) {
      activateHoverPair(card, wrapper, primary, secondary);
      return;
    }

    if (hoverIntersectionObserver) hoverIntersectionObserver.observe(card);
  };

  const registerHoverCards = (root = document) => {
    if (!(root instanceof Document || root instanceof Element || root instanceof DocumentFragment)) return;

    if (root instanceof Element && root.matches(hoverCardSelector)) registerHoverCard(root);
    root.querySelectorAll?.(hoverCardSelector).forEach(registerHoverCard);

    root.querySelectorAll?.('a[href*="/products/"]').forEach((link) => {
      const card = link.closest(
        '[data-product-card], [data-product-url], [data-corzo-catalog-card], .resource-card, .product-card, product-card'
      );
      if (card) registerHoverCard(card);
    });
  };

  registerHoverCards(document);

  const hoverMutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        registerHoverCards(node);
        const parentCard = node.closest(hoverCardSelector);
        if (parentCard && parentCard.dataset.corzoHoverReady !== 'true') prepareHoverCard(parentCard);
      });
    });
  });
  hoverMutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  controller.signal.addEventListener('abort', () => {
    hoverMutationObserver.disconnect();
    hoverIntersectionObserver?.disconnect();
  }, { once: true });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const card = event.target instanceof Element ? event.target.closest(hoverCardSelector) : null;
    if (card) prepareHoverCard(card);
  }, { signal: controller.signal, capture: true, passive: true });

  document.addEventListener('focusin', (event) => {
    const card = event.target instanceof Element ? event.target.closest(hoverCardSelector) : null;
    if (card) prepareHoverCard(card);
  }, { signal: controller.signal, capture: true });
  /* END DYNASTY GLOBAL PRODUCT-CARD RULE */

  /* DYNASTY close options when pointer leaves card v2026-08-10 */
  document.addEventListener('pointerout', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;

    const card = getCard(event.target);
    if (!card) return;

    const nextTarget = event.relatedTarget;
    if (nextTarget && card.contains(nextTarget)) return;

    window.requestAnimationFrame(() => {
      if (card.matches(':hover')) return;
      const panel = card.querySelector('[data-corzo-options-panel]:not([hidden])');
      if (panel) closePanel(panel);
    });
  }, { signal: controller.signal, capture: true });
  /* END DYNASTY pointer-leave behavior v2026-08-10 */

  let scrollCloseFrame = 0;
  window.addEventListener('scroll', () => {
    if (scrollCloseFrame) return;

    scrollCloseFrame = window.requestAnimationFrame(() => {
      scrollCloseFrame = 0;
      document.querySelectorAll('[data-corzo-options-panel]:not([hidden])').forEach((panel) => {
        if (panel.dataset.corzoUserSelected !== 'true') {
          closePanel(panel);
        }
      });
    });
  }, { signal: controller.signal, passive: true });

  /* CORZO protected header scope v2026-08-12 */
  window.corzoHeaderTransitionObserver?.disconnect();

  const corzoHeader = document.querySelector('#header-component');
  const corzoMain = document.querySelector('#MainContent');
  const corzoIsHome = corzoMain?.dataset.template === 'index';

  if (corzoHeader) {
    corzoHeader.dataset.corzoPage = corzoIsHome ? 'home' : 'interior';

    if (!corzoIsHome) {
      /*
       * CORZO restored interior transition v2026-08-12.
       * It only changes the header state after scroll; all catalog content remains untouched.
       */
      const corzoPageWrapper = document.querySelector('.page-wrapper');
      let corzoInteriorFrame = 0;

      const getCorzoInteriorScrollTop = () => Math.max(
        window.scrollY || 0,
        document.documentElement?.scrollTop || 0,
        document.scrollingElement?.scrollTop || 0,
        corzoPageWrapper?.scrollTop || 0
      );

      const updateCorzoInteriorHeader = () => {
        corzoInteriorFrame = 0;
        const isCompact = getCorzoInteriorScrollTop() > 36;
        corzoHeader.dataset.corzoHeaderMode = isCompact ? 'bar' : 'default';
        corzoHeader.dataset.scrollDirection = isCompact ? 'down' : 'none';

        if (isCompact) {
          corzoHeader.dataset.stickyState = 'active';
        } else {
          delete corzoHeader.dataset.stickyState;
        }
      };

      const scheduleCorzoInteriorHeader = () => {
        if (corzoInteriorFrame) return;
        corzoInteriorFrame = window.requestAnimationFrame(updateCorzoInteriorHeader);
      };

      window.addEventListener('scroll', scheduleCorzoInteriorHeader, {
        signal: controller.signal,
        passive: true
      });
      corzoPageWrapper?.addEventListener('scroll', scheduleCorzoInteriorHeader, {
        signal: controller.signal,
        passive: true
      });
      window.addEventListener('resize', scheduleCorzoInteriorHeader, {
        signal: controller.signal,
        passive: true
      });

      updateCorzoInteriorHeader();
    } else {
      const corzoHero = document.querySelector(
        '.content-for-layout > .shopify-section:first-child .slideshow-section'
      );
      const corzoPageWrapper = document.querySelector('.page-wrapper');
      let corzoHeaderFrame = 0;
      const hasHero = Boolean(corzoHero);
      const fallbackThreshold = Math.max(96, Math.min(180, window.innerHeight * 0.16));

      const getCorzoScrollTop = () => Math.max(
        window.scrollY || 0,
        document.documentElement?.scrollTop || 0,
        document.scrollingElement?.scrollTop || 0,
        corzoPageWrapper?.scrollTop || 0
      );

      const getCorzoThreshold = (scrollTop) => {
        if (!corzoHero) return fallbackThreshold;
        const heroTop = corzoHero.getBoundingClientRect().top + scrollTop;
        return Math.max(0, heroTop + (corzoHero.offsetHeight * 0.5));
      };

      const updateCorzoHeaderMode = () => {
        corzoHeaderFrame = 0;
        const scrollTop = getCorzoScrollTop();
        const nextMode = scrollTop >= getCorzoThreshold(scrollTop) ? 'bar' : 'transparent';
        corzoHeader.dataset.corzoHeaderMode = nextMode;
        corzoHeader.dataset.corzoHasHero = hasHero ? 'true' : 'false';

        if (nextMode === 'bar') {
          corzoHeader.dataset.stickyState = 'active';
          corzoHeader.dataset.scrollDirection = 'down';
        } else {
          delete corzoHeader.dataset.stickyState;
          corzoHeader.dataset.scrollDirection = 'none';
        }
      };

      const scheduleCorzoHeaderUpdate = () => {
        if (corzoHeaderFrame) return;
        corzoHeaderFrame = window.requestAnimationFrame(updateCorzoHeaderMode);
      };

      window.addEventListener('scroll', scheduleCorzoHeaderUpdate, {
        signal: controller.signal,
        passive: true
      });
      corzoPageWrapper?.addEventListener('scroll', scheduleCorzoHeaderUpdate, {
        signal: controller.signal,
        passive: true
      });
      window.addEventListener('resize', scheduleCorzoHeaderUpdate, {
        signal: controller.signal,
        passive: true
      });

      if (corzoHero) {
        window.corzoHeaderTransitionObserver = new ResizeObserver(scheduleCorzoHeaderUpdate);
        window.corzoHeaderTransitionObserver.observe(corzoHero);
      }

      updateCorzoHeaderMode();
    }
  }
  /* END CORZO protected header scope v2026-08-12 */

})();
